import type {ReactiveController, ReactiveControllerHost} from "lit";

import {nextSkin, resolveSkin, type GimmickName, type SkinOf} from "../../domain/gimmicks/gimmick-registry";
import {loadViewerSkin, saveViewerSkin, subscribeViewerSkin} from "../../domain/gimmicks/gimmick-skin.store";

/** What a gimmick element offers the controller: its tile id and the look stored with the tile, raw. */
export interface GimmickSkinHost extends ReactiveControllerHost {
    readonly tileId: string;
    readonly savedSkin: string;
    readonly isConnected: boolean;
}

/**
 * The look ONE viewer sees on a gimmick tile (feature plans 0071, 0072), shared by every gimmick element:
 * `savedSkin` is the look stored with the tile (dialog); the tile's "Optik wechseln" button switches it for
 * this viewer only (`gimmick-skin.store.ts`), and their choice lapses once someone changes the saved look.
 * Another tab of the same browser follows through the `storage` event.
 *
 * A controller gets no `changedProperties`, so it remembers `tileId` and `savedSkin` itself and compares in
 * `hostUpdate` — and only while the host is connected (a disconnected host listens to nothing). A new tile id
 * changes the storage key: subscribe anew AND reload. A new saved look: reload (the viewer's choice lapses).
 */
export class GimmickSkinController<N extends GimmickName> implements ReactiveController {

    private _raw = '';
    private _tileId: string | null = null;
    private _savedSkin: string | null = null;
    private _unsubscribe: (() => void) | null = null;

    public constructor(private readonly host: GimmickSkinHost, private readonly gimmick: N) {
        host.addController(this);
    }

    /** The look to draw — the viewer's choice or the saved look, an unknown value as the default. */
    public get skin(): SkinOf<N> {
        return resolveSkin(this.gimmick, this._raw);
    }

    /** The "Optik wechseln" button: the next look, for this viewer only. */
    public next(): void {
        const skin = nextSkin(this.gimmick, this.skin);
        this._raw = skin;
        saveViewerSkin(this.gimmick, this.host.tileId, skin, this.host.savedSkin);
        this.host.requestUpdate();
    }

    public hostConnected(): void {
        this._listen();
        this._load();
    }

    public hostUpdate(): void {
        if (!this.host.isConnected) {
            return;
        }
        if (this.host.tileId !== this._tileId) {
            this._listen();
            this._load();
        } else if (this.host.savedSkin !== this._savedSkin) {
            this._load();
        }
    }

    public hostDisconnected(): void {
        this._unsubscribe?.();
        this._unsubscribe = null;
    }

    private _listen(): void {
        this._unsubscribe?.();
        this._tileId = this.host.tileId;
        this._unsubscribe = this._tileId
            ? subscribeViewerSkin(this.gimmick, this._tileId, () => {
                this._load();
                this.host.requestUpdate();
            })
            : null;
    }

    private _load(): void {
        this._savedSkin = this.host.savedSkin;
        this._raw = loadViewerSkin(this.gimmick, this.host.tileId, this.host.savedSkin);
    }
}
